/*
 * Copyright (c) 2020
 *    Reliant Business Valuation, LLC - http://www.reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Component, OnInit, OnDestroy, Input, ViewChild, Output, EventEmitter, TemplateRef } from '@angular/core';
import { Report, State, Stage, HistoryStage, Helpers, User } from '../../../../../model';
import { HolidayDates } from '../../../../../model/helper/holiday-dates';
import { StageService, ModeService, UserService, LogService } from 'src/app/services';
import { Router } from '@angular/router';
import { ReportProvider, TagProvider } from 'src/app/providers';
import { StageChangeRequest, ReportUsers, ReportDelay, ReportType } from 'src/app/model/report';
import { ReportService } from 'src/app/services/report.service';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { ConfirmNextStageModal } from '../../modal/confirm-stage/confirm-next-stage.modal';
import { Subject } from 'rxjs';
import { ReportStageModal, ReportDelayModal, ConfirmRevertModal, ReportFollowUpModal, ChangeResearchedSpreadedModal } from '../../../report/modals';
import { ReportNote } from 'src/app/model/report.note';
import { ReportNotesModal } from '../../modal/notes/report-notes.modal';
import { ConfirmDeleteReportModal } from '../../modal/confirm-delete/confirm-delete-report.modal';
import { ConfirmationDialogComponent, CrmAlertComponent } from 'src/app/components/fields';
import { Tag } from 'src/app/model/tag';
import { SystemType } from 'src/app/model/enums/system.type';
import { DuplicateReportModal } from 'src/app/components/modal';
import * as _ from 'lodash';
import { ClipboardService } from 'ngx-clipboard';
import { ReportFollowUp } from 'src/app/model/report.followup';
import { BVQReportProvider } from 'src/app/providers/bvq';
import { ReportSourceService } from 'src/app/modules/bvq/services';
import { MenuItem } from 'src/app/model/menu-item';
import { RightClickMenu } from 'src/app/model/enums/right-click-menu.enum';
import { AssignAnalystModal } from '../../modal/assign-analyst/assign-analyst.modal';

declare var moment: any;

@Component({
  selector: 'report-row',
  templateUrl: 'report-row.component.html',
  styleUrls: ['report-row.component.scss']
})

export class ReportRowComponent implements OnInit, OnDestroy {
  @ViewChild('alert') alert: CrmAlertComponent;
  @Input() report: Report;
  @Input() rowStyle: string = 'L';
  @Input() isLast: boolean = false;
  @Input() tags: Tag[] = [];
  @Output() reportDeleted: EventEmitter<boolean> = new EventEmitter();
  @Output() filterTagChanged: EventEmitter<Tag> = new EventEmitter();
  @Output() quoteUpdated: EventEmitter<Tag> = new EventEmitter();
  @Output() reportUpdated: EventEmitter<Report> = new EventEmitter();

  isSingleClick: boolean = true;
  copied: boolean = false;

  today: Date = new Date();
  nextStageId: string;
  stages: Stage[];
  deleteModal: BsModalRef;
  noteModal: BsModalRef;
  reverModal: BsModalRef;
  changeStageModal: BsModalRef;
  confirmStageModal: BsModalRef;
  exportModalRef: BsModalRef;
  currentMode: SystemType;
  modeSub: any;
  stagesSub: any;
  SystemType = SystemType;
  user: User;
  userSub: any;
  duplicateReportModal: BsModalRef;
  quoteRef: BsModalRef;
  modalRef: BsModalRef;
  confirmationModal: BsModalRef;
  ReportType = ReportType;

  dayDiff: number = 0;

  /** Menu items used on Right click of Field item */
  menuItems: MenuItem[] = [
    MenuItem.createItem('fa-chrome', 'Open in new tab', RightClickMenu.OPEN_IN_NEW_TAB),
  ]

  constructor(
    private router: Router,
    private stageService: StageService,
    public reportProvider: ReportProvider,
    private reportService: ReportService,
    private modalService: BsModalService,
    private modeService: ModeService,
    private userService: UserService,
    private clipboardService: ClipboardService,
    private bvqProvider: BVQReportProvider,
    private tagProvider: TagProvider,
    private sourceService: ReportSourceService,
    private logService : LogService
  ) {
  }


  ngOnInit(): void {
    this.modeService.mode$.subscribe(mode => this.currentMode = mode);
    this.userSub = this.userService.user$.subscribe(u => this.user = u);
    this.stagesSub = this.stageService.stages$.subscribe(stage => this.stages = stage);
  }

  ngOnDestroy(): void {
    if (this.modeSub)
      this.modeSub.unsubscribe();
    if (this.userSub)
      this.userSub.unsubscribe();
    if (this.stagesSub)
      this.stagesSub.unsubscribe();
  }

  getNextStage(report: Report): Stage {
    this.nextStageId = this.stageService.getNextStageId(report.STAGE.ID);
    if (this.stages)
      return this.stages.filter(stage => stage.ID === this.nextStageId)[0];
    else return null;
  }

  clickEvent(report: Report): void {
    this.isSingleClick = true;
    setTimeout(() => {
      if (this.isSingleClick)
        this.goReport(report);
    }, 400);
  }

  doubleClickEvent(report: Report): void {
    this.isSingleClick = false;

    let companyName = report.IS_QUOTE ? (report.REPORT_QUOTE?.COMPANY_NAME ? report.REPORT_QUOTE?.COMPANY_NAME : 'Corporate Name not available') : report.primaryEntity(this.user);
    this.clipboardService.copyFromContent(companyName);
    this.copied = true;

    setTimeout(() => {
      this.copied = false;
    }, 3000);
  }

  goReport(report: Report): void {
    report.isLoading = true;

    this.reportProvider.getReportById(report.ID)
      .subscribe(data => {
        if (data.success) {
          report.isLoading = false;
          report = Report.parse(data.result);

          if (report.IS_QUOTE) {
            this.router.navigate(['/processing/quote', report.ID], { state: { report: data.result } });
          }
          else {
            if (this.currentMode === SystemType.BV) {
              this.router.navigate(['/report', report.ID, 'general'], { state: { report: data.result } });
            }
            else if (this.currentMode === SystemType.EA) {
              this.router.navigate(['/report-ea', report.ID, 'general'], { state: { report: data.result } });
            }
          }
        }
      });
  }

  onNextStage(report: Report): void {
    let stageRequest: StageChangeRequest = new StageChangeRequest();
    let users: ReportUsers;

    this.reportProvider.getReportUsers(report.ID)
      .subscribe(data => {
        if (data.success) {

          users = ReportUsers.parse(data.result);
          stageRequest = this.reportService.getReportStageRequest(this.nextStageId, users);

          if (stageRequest.ASSIGNEE_TYPE_USER) {

            this.confirmStageModal = this.modalService.show(ConfirmNextStageModal, Helpers.getModalConfig());
            const ackSubject = new Subject<Boolean>();
            this.confirmStageModal.content.subject = ackSubject;

            ackSubject.subscribe(result => {
              if (result) {
                this.reportProvider.setStage(report.ID, stageRequest)
                  .subscribe(historyData => {
                    if (historyData.success) {
                      const history: HistoryStage = HistoryStage.parse(historyData.result);
                      report.STAGE = this.stageService.getStageById(history.SID);
                      report.ASSIGNEE = history.ASSIGNEE;
                      report.STATE = history.STATE;
                    }
                  });
              }
            });

          } else {

            this.changeStageModal = this.modalService.show(ReportStageModal, Helpers.getModalConfig());
            const ackSubject = new Subject<HistoryStage>();
            this.changeStageModal.content.subject = ackSubject;
            this.changeStageModal.content.reportId = report.ID;
            this.changeStageModal.content.nextStageId = this.nextStageId;
            this.changeStageModal.content.userList = users;
            this.changeStageModal.content.disabledInput = false;
            this.changeStageModal.content.setReportUsers();
            this.changeStageModal.content.getReportUser();
            this.changeStageModal.content.setStageList();

            ackSubject.subscribe(value => {
              if (value) {
                report.STAGE = this.stageService.getStageById(value.SID);
                report.ASSIGNEE = value.ASSIGNEE;
                report.STATE = value.STATE;
              }
            });
          }
        }
      });
  }

  setReportNote(report: Report): void {
    this.noteModal = this.modalService.show(ReportNotesModal, Helpers.getModalConfig());
    const ackSubject = new Subject<ReportNote>();
    this.noteModal.content.subject = ackSubject;
    this.noteModal.content.report = report;

    ackSubject.subscribe(value => {
    });
  }

  delayReport(report: Report): void {
    let delay: ReportDelay = new ReportDelay();
    this.reportProvider.getReportDelay(report.ID).subscribe(data => {
      if (data.success) {
        delay = ReportDelay.parse(data.result);
        report.ACTIVE_DELAY = delay;
        this.reportService.report.next(report);
      }
    });

    this.reverModal = this.modalService.show(ReportDelayModal, Helpers.getModalConfig());
    const ackSubject = new Subject<Boolean>();
    this.reverModal.content.subject = ackSubject;
    ackSubject.subscribe(result => {
      if (result) {
        this.logService.log(result);
      }
    });
  }

  revertReport(report: Report): void {
    this.reverModal = this.modalService.show(ConfirmRevertModal, Helpers.getModalConfig());
    const ackSubject = new Subject<Boolean>();
    this.reverModal.content.subject = ackSubject;

    ackSubject.subscribe(result => {
      if (result) {
        this.reportProvider.revertReport(report.ID)
          .subscribe(data => {
            if (data.success) {
              const historyStage = HistoryStage.parse(data.result);

              if (historyStage) {

                const stage = this.stageService.getStageById(historyStage.SID);

                report.STAGE = stage;

                if (historyStage.STATE) {
                  report.STATE = historyStage.STATE;
                }

                if (historyStage.ASSIGNEE) {
                  report.ASSIGNEE = historyStage.ASSIGNEE;
                }

              }
            }
          });
      }
    });
  }

  deleteReport(report: Report): void {
    this.alert.hide();
    this.deleteModal = this.modalService.show(ConfirmDeleteReportModal, Helpers.getModalConfig());
    const ackSubject = new Subject<boolean>();
    this.deleteModal.content.subject = ackSubject;
    this.deleteModal.content.report = report;

    ackSubject.subscribe(value => {
      if (value) {
        this.reportProvider.deleteReport(report.ID)
          .subscribe(
            data => {
              if (data.success) {
                let reportID: any = report.ID;
                this.reportDeleted.emit(reportID);
              }
              else
                this.alert.show(data.error);
              setTimeout(() => {
                this.alert.hide();
              }, 3000);
            });
      }
    });
  }

  /**
   * Open modal to export
   * @param template
  */
  openPageExportModal(template: TemplateRef<any>): void {
    this.exportModalRef = this.modalService.show(template, { backdrop: 'static' });
  }

  onFilterChanged($event: any, tag: Tag): void {
    $event.stopPropagation();
    this.filterTagChanged.emit(tag);
  }

  dateDiff(date: any, add: number = null): number {
    if (add)
      return moment(date).add(add, 'days').diff(moment(), 'days');
    else return moment(date).diff(moment(), 'days');
  }

  dateDifference(date: string, date2: string): number {
    return moment(date2).diff(moment(date), 'days');
  }

  dateVal(date: any, add: number = null): number {
    if (add)
      return moment(date).add(add, 'days').diff(moment(), 'days');
    else return moment(date).diff(moment(), 'days');
  }

  dateAdd(date: any, add: number): Date {
    let dueDate = moment(date);
    let days = add;

    while (days > 0) {
      dueDate = dueDate.add(1, 'days');
      if (dueDate.isoWeekday() !== 6 && dueDate.isoWeekday() !== 7 && !HolidayDates.isHoliday(dueDate)) {
        days -= 1;
      }
    }

    return moment(dueDate).toDate();
  }

  showDeadline(state: State): boolean {
    return !(state.ID === 'CMP' || state.ID === 'CCD' || state.ID === 'LST');
  }

  isOverExpired(): boolean {
    return this.dateDiff(this.report.getDueDate(), this.report.turnDiff) < 0;
  }

  onDuplicate(report: Report): void {
    let config = Helpers.getModalConfig();
    config['report'] = _.cloneDeep(report);
    this.duplicateReportModal = this.modalService.show(DuplicateReportModal, config);
    const ackSubject = new Subject<any>();
    this.duplicateReportModal.content.subject = ackSubject;
    ackSubject.subscribe(value => {
      if (value.ACCEPT) {
        this.reportProvider.duplicateReport(report.ID, value.ENTITIES, value.INTERNAL_FILE_NAME)
          .subscribe(
            data => {
              if (data.success) {
                this.reportService.setReport(Report.parse(data.result));

                if (this.currentMode === SystemType.BV)
                  this.router.navigate(['/report', data.result.ID, 'general']);
                else if (this.currentMode === SystemType.EA)
                  this.router.navigate(['/report-ea', data.result.ID, 'general']);
              }
            });
      }
    });
  }

  changeStage(report: Report): void {
    this.reportProvider.getReportUsers(report.ID)
      .subscribe(data => {
        if (data.success) {
          let users: ReportUsers = ReportUsers.parse(data.result);
          this.changeStageModal = this.modalService.show(ReportStageModal, Helpers.getModalConfig());
          const ackSubject = new Subject<HistoryStage>();
          this.changeStageModal.content.subject = ackSubject;
          this.changeStageModal.content.reportId = this.report.ID;
          this.changeStageModal.content.nextStageId = this.stageService.getNextStageId(this.report.STAGE.ID);
          this.changeStageModal.content.userList = users;
          this.changeStageModal.content.setReportUsers();
          this.changeStageModal.content.getReportUser();
          this.changeStageModal.content.setStageList();

          ackSubject.subscribe(value => {
            if (value) {
              this.report.STAGE = this.stageService.getStageById(value.SID);
              this.report.ASSIGNEE = value.ASSIGNEE;
              this.report.STATE = value.STATE;

              if (value.SID === 'ENGD' || value.SID === 'EAED') {
                report.HAS_BVQ = true;
              }
            }
          });
        }
      });
  }

  /**Create Follow Up */
  setFollowUp(report: Report): void {
    this.modalRef = this.modalService.show(ReportFollowUpModal, Helpers.getModalConfig());
    const ackSubject = new Subject<ReportFollowUp>();
    this.modalRef.content.subject = ackSubject;
    this.modalRef.content.report = report;

    ackSubject.subscribe(value => {
      if (value) {
        // report.FOLLOW_UPS.push(value);
      }
    });
  }

  /** Create BVQ */
  createBVQ(report: Report): void {
    this.bvqProvider.createReport(report.ID)
      .subscribe(data => {
        if (data.success) {
          this.router.navigate(['/bvq/report', report.ID, 'overview']);
        }
      });
  }

  /** View in BVQ */
  viewInBVQ(report: Report): void {
    if (report.HAS_BVQ == true) {
      report.isLoading = true;

      this.bvqProvider.getReportById(report.ID)
        .subscribe(data => {
          if (data.success) {
            report.isLoading = false;
            report = Report.parse(data.result);
            this.sourceService.setSource(null, null);
            this.router.navigate(['/bvq/report', report.ID, 'overview'],
              { state: { report: data.result } });
          }
        });
    }
  }

  /** View in BVQ - Transactional Database */
  viewInBVQDatabase(report: Report): void {
    if (report.HAS_BVQ == true) {
      report.isLoading = true;

      this.bvqProvider.getReportById(report.ID)
        .subscribe(data => {
          if (data.success) {
            report.isLoading = false;
            report = Report.parse(data.result);
            this.sourceService.setSource(null, null);
            this.router.navigate(['/bvq/report', report.ID, 'overview', 'database'],
              { state: { report: data.result } });
          }
        });
    }
  }

  setResearched(report: Report): void {
    this.confirmationModal = this.modalService.show(ConfirmationDialogComponent, Helpers.getModalConfig());
    const ackSubject = new Subject<boolean>();
    this.confirmationModal.content.subject = ackSubject;
    this.confirmationModal.content.title = 'Set Report Researched';
    this.confirmationModal.content.text = 'Do you really want to set Report Researched?';
    this.confirmationModal.content.actionButton = 'Confirm';
    this.confirmationModal.content.actionButtonClass = 'warning';

    ackSubject.subscribe(value => {
      if (value) {
        this.reportProvider.save(report.ID, 'R_RESEARCH', this.user.ID)
          .subscribe(data => {
            if (data.success) {
              report.R_RESEARCH = this.user;
            }
          });
      }
    });
  }

  setSpreaded(report: Report): void {
    this.confirmationModal = this.modalService.show(ConfirmationDialogComponent, Helpers.getModalConfig());
    const ackSubject = new Subject<boolean>();
    this.confirmationModal.content.subject = ackSubject;
    this.confirmationModal.content.title = 'Set Report Spreaded';
    this.confirmationModal.content.text = 'Do you really want to set Report Spreaded?';
    this.confirmationModal.content.actionButton = 'Confirm';
    this.confirmationModal.content.actionButtonClass = 'warning';

    ackSubject.subscribe(value => {
      if (value) {
        this.reportProvider.save(report.ID, 'R_SPREAD', this.user.ID)
          .subscribe(data => {
            if (data.success) {
              report.R_SPREAD = this.user;
            }
          });
      }
    });
  }

  changeResearched(report: Report): void {
    this.modalRef = this.modalService.show(ChangeResearchedSpreadedModal, Helpers.getModalConfig());
    const ackSubject = new Subject<any>();
    this.modalRef.content.subject = ackSubject;
    this.modalRef.content.report = report;
    this.modalRef.content.field = 'R_RESEARCH';
    this.modalRef.content.selectedUser = report.R_RESEARCH;
    ackSubject.subscribe(value => {
      if (value) {
        if (value.ACCEPT) {
          report.R_RESEARCH = value.USER;
        }
      }
    });
  }

  changeSpreaded(report: Report): void {
    this.modalRef = this.modalService.show(ChangeResearchedSpreadedModal, Helpers.getModalConfig());
    const ackSubject = new Subject<any>();
    this.modalRef.content.subject = ackSubject;
    this.modalRef.content.report = report;
    this.modalRef.content.field = 'R_SPREAD';
    this.modalRef.content.selectedUser = report.R_SPREAD;
    ackSubject.subscribe(value => {
      if (value) {
        if (value.ACCEPT) {
          report.R_SPREAD = value.USER;
        }
      }
    });
  }

  setQuickTags(report: Report, tag: Tag): void {
    let found = report.TAGS.find(t => t.ID === tag.ID);

    if (found) {
      this.tagProvider.deleteReportTag(tag, report.ID)
        .subscribe(data => {
          if (data.success) {
            report.TAGS = report.TAGS.filter((fltr) => { if (fltr.ID != found.ID) return fltr; });
          }
        }, err => {
          this.logService.error(err);
        });
    }
    else {
      this.tagProvider.addReportTag(tag, report.ID)
        .subscribe(data => {
          if (data.success) {
            this.report.TAGS.push(tag);
          }
        }, err => {
          this.logService.error(err);
        });
    }

  }

  /** Track by function for company name. */
  trackByFn(index, item): any {
    return (item.id);
  }

  // Calculate day difference
  calculateDayDiff(): void {
    let date = moment(this.report.DATE_ENGAGED);
    this.dayDiff = moment().diff(moment(this.report.DATE_ENGAGED), 'days');

    let i = this.dayDiff;

    while (i > 0) {
      if (date.isoWeekday() == 6 || date.isoWeekday() == 7 || HolidayDates.isHoliday(date))
        this.dayDiff -= 1;

      date = date.add(1, 'days');
      i--;
    }
  }

  assigneAnalyst(report: Report): void {
    this.modalRef = this.modalService.show(AssignAnalystModal, Helpers.getModalConfig());
    const ackSubject = new Subject<any>();
    this.modalRef.content.subject = ackSubject;
    this.modalRef.content.report = report;
    ackSubject.subscribe(value => {
      if (value) {
        if (value.ACCEPT) {
          report.ASSIGNEE = value.USER;
          this.reportUpdated.emit(report)
        }
      }
    });
  }


  showUnassigned(): boolean {
    if (this.report.USERS?.FINANCIAL_ANALYST == null)
      return true;

    return false;
  }
}


